/**
 * Запись экрана для отзыва: картинка вкладки/окна/экрана + звук системы (вкладки) + микрофон
 * сводятся в один файл. Размер держим маленьким: до 1080p, 15 кадров/с, битрейт от разрешения
 * (~1 Мбит/с для 1080p — текст интерфейса читается, минута ≈ 8–10 МБ). Длительность в WebM
 * дописывается здесь же (webm-duration.ts): файл уходит в API v3 мимо сервера.
 */
import { fixWebmDuration } from "./webm-duration"
import { showInteractionOverlay } from "./interaction-marks"

export interface RecordingResult {
  file: File
  durationSec: number
  hasMic: boolean
  hasSystemAudio: boolean
}

export interface RecorderHandle {
  stop: () => void
  cancel: () => void
  pause: () => void
  resume: () => void
  readonly paused: boolean
  readonly startedAt: number
  readonly hasMic: boolean
  readonly hasSystemAudio: boolean
  /** Причина, по которой не удалось подключить микрофон */
  readonly micError: string | null
  /** display — getDisplayMedia, dom — кадры страницы (см. dom-capture.ts) */
  readonly mode: "display" | "dom"
  /** Что выбрал пользователь в getDisplayMedia: browser, window, monitor */
  readonly displaySurface: string | null
}

export const MAX_RECORDING_SEC = 10 * 60

const MIME_CANDIDATES = [
  "video/webm;codecs=vp9,opus",
  "video/webm;codecs=vp8,opus",
  "video/webm",
  // High 4.0 держит кадр до 1920×1080; Baseline 3.1 — только до 1280×720, запасной вариант
  "video/mp4;codecs=avc1.640028,mp4a.40.2",
  "video/mp4;codecs=avc1.42E01F,mp4a.40.2",
  "video/mp4",
]

type DisplayMediaOptions = DisplayMediaStreamOptions & Record<string, unknown>

export function screenCaptureSupported(): boolean {
  return typeof navigator !== "undefined" && !!navigator.mediaDevices?.getDisplayMedia && typeof MediaRecorder !== "undefined"
}

export function pickMimeType(isSupported: (m: string) => boolean = (m) => MediaRecorder.isTypeSupported(m)): string | null {
  return MIME_CANDIDATES.find((m) => isSupported(m)) ?? null
}

/**
 * Битрейт записи страницы (DOM → canvas): кадров мало, но в них мелкий текст, и при битрейте «на кадр»
 * кодек его размывает. ~1.5 бита на пиксель кадра, 2–6 Мбит/с.
 */
export function screenBitrate(width: number, height: number): number {
  return Math.round(Math.min(6_000_000, Math.max(2_000_000, width * height * 1.5)))
}

/** Битрейт видео: ~0.045 бит на пиксель кадра, 0.5–2.5 Мбит/с */
export function videoBitrate(width: number, height: number, fps: number): number {
  return Math.round(Math.min(2_500_000, Math.max(500_000, width * height * fps * 0.045)))
}

const RECORDING_MAX = { width: 1920, height: 1080 }
const SCREENSHOT_MAX = { width: 3840, height: 2160 }
const RECORDING_FPS = { ideal: 15, max: 15 }

/** Размер, вписанный в рамку с сохранением пропорций; меньший кадр не увеличивается */
export function fitSize(width: number, height: number, maxWidth: number, maxHeight: number): { width: number; height: number } {
  const k = Math.min(1, maxWidth / width, maxHeight / height)
  return { width: Math.round(width * k), height: Math.round(height * k) }
}

/** Размер первого кадра дорожки: getSettings() в Safari до первого кадра отдаёт 640×480 */
async function frameSize(el: HTMLVideoElement, timeoutMs = 3000): Promise<{ width: number; height: number } | null> {
  if (!el.videoWidth) {
    await Promise.race([
      new Promise<void>((r) => el.addEventListener("loadedmetadata", () => r(), { once: true })),
      new Promise<void>((r) => setTimeout(r, timeoutMs)),
    ])
  }
  return el.videoWidth && el.videoHeight ? { width: el.videoWidth, height: el.videoHeight } : null
}

/**
 * Предел размера кадра ставится после первого кадра, а не в getDisplayMedia: Safari (ScreenCaptureKit), пока не
 * знает размер выбранного экрана, считает его 640×480, и width/height { max } в getDisplayMedia навсегда
 * оставляли запись всего экрана шириной 640 px.
 */
async function limitFrameSize(
  track: MediaStreamTrack,
  el: HTMLVideoElement,
  max: { width: number; height: number },
  extra: MediaTrackConstraints = {},
): Promise<{ width: number; height: number }> {
  const s = track.getSettings()
  const real = (await frameSize(el)) ?? { width: s.width ?? max.width, height: s.height ?? max.height }
  const fit = fitSize(real.width, real.height, max.width, max.height)
  if (fit.width < real.width) {
    try {
      await track.applyConstraints({ ...extra, width: { max: fit.width }, height: { max: fit.height } })
    } catch {
      return real
    }
  }
  return fit
}

function previewElement(stream: MediaStream): HTMLVideoElement {
  const el = document.createElement("video")
  el.muted = true
  el.playsInline = true
  el.srcObject = stream
  return el
}

function displayOptions(audio: boolean): DisplayMediaOptions {
  return {
    video: { frameRate: RECORDING_FPS },
    audio: audio ? { echoCancellation: false, noiseSuppression: false, autoGainControl: false } : false,
    // Chrome: в выборе по умолчанию вкладки, текущую вкладку можно выбрать, звук системы предлагается
    displaySurface: "browser",
    selfBrowserSurface: "include",
    systemAudio: "include",
    surfaceSwitching: "include",
  }
}

function describeError(e: unknown, source: "screen" | "mic" = "mic"): string {
  const name = (e as { name?: string })?.name
  if (name === "NotAllowedError") return source === "mic" ? "доступ к микрофону запрещён" : "доступ запрещён"
  if (name === "NotFoundError") return source === "mic" ? "микрофон не найден" : "источник не найден"
  // macOS без разрешения «Запись экрана» для браузера отдаёт именно эту ошибку
  if (name === "NotReadableError")
    return source === "mic" ? "микрофон занят другим приложением" : "браузер не смог захватить экран — проверьте, что ему разрешена запись экрана в настройках системы"
  return (e as Error)?.message || "неизвестная ошибка"
}

export class CaptureCancelled extends Error {
  constructor() {
    super("Запись отменена")
  }
}

/**
 * Начать запись. Бросает CaptureCancelled, если пользователь закрыл окно выбора экрана.
 * onDone вызывается после stop() или когда пользователь нажал «Прекратить доступ» в браузере.
 */
export async function startScreenRecording(opts: { mic: boolean; onDone: (r: RecordingResult) => void; onError: (message: string) => void }): Promise<RecorderHandle> {
  if (!screenCaptureSupported()) throw new Error("Браузер не умеет записывать экран")
  let display: MediaStream
  try {
    display = await navigator.mediaDevices.getDisplayMedia(displayOptions(true))
  } catch (e) {
    if ((e as { name?: string })?.name === "NotAllowedError" || (e as { name?: string })?.name === "AbortError") throw new CaptureCancelled()
    throw new Error(`Не удалось начать запись экрана: ${describeError(e, "screen")}`)
  }
  const video = display.getVideoTracks()[0]
  if ("contentHint" in video) video.contentHint = "detail"
  const preview = previewElement(new MediaStream([video]))
  void preview.play().catch(() => {})
  const size = await limitFrameSize(video, preview, RECORDING_MAX, { frameRate: RECORDING_FPS })
  preview.pause()
  preview.srcObject = null

  let mic: MediaStream | null = null
  let micError: string | null = null
  if (opts.mic) {
    try {
      mic = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } })
    } catch (e) {
      micError = describeError(e)
    }
  }

  const systemTracks = display.getAudioTracks()
  const micTracks = mic?.getAudioTracks() ?? []
  let audioCtx: AudioContext | null = null
  const tracks: MediaStreamTrack[] = [video]
  if (systemTracks.length || micTracks.length) {
    audioCtx = new AudioContext()
    const dest = audioCtx.createMediaStreamDestination()
    for (const t of [...systemTracks, ...micTracks]) audioCtx.createMediaStreamSource(new MediaStream([t])).connect(dest)
    tracks.push(...dest.stream.getAudioTracks())
  }
  const stream = new MediaStream(tracks)

  const mime = pickMimeType()
  const s = video.getSettings()
  const recorder = new MediaRecorder(stream, {
    ...(mime ? { mimeType: mime } : {}),
    videoBitsPerSecond: videoBitrate(size.width, size.height, Math.min(15, s.frameRate ?? 15)),
    audioBitsPerSecond: 64_000,
  })
  const chunks: Blob[] = []
  const startedAt = Date.now()
  let pausedMs = 0
  let pausedAt: number | null = null
  let cancelled = false
  let finished = false
  let hideMarks: (() => void) | null = null

  const release = () => {
    hideMarks?.()
    hideMarks = null
    for (const t of [...display.getTracks(), ...(mic?.getTracks() ?? [])]) t.stop()
    void audioCtx?.close().catch(() => {})
    clearTimeout(limit)
  }

  recorder.ondataavailable = (e) => e.data.size && chunks.push(e.data)
  recorder.onerror = (e) => {
    release()
    opts.onError(`Запись прервалась: ${describeError((e as unknown as { error?: unknown }).error, "screen")}`)
  }
  recorder.onstop = () => {
    release()
    if (cancelled || finished) return
    finished = true
    const type = recorder.mimeType || mime || "video/webm"
    const ext = type.includes("mp4") ? "mp4" : "webm"
    const stamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, "-")
    const raw = new Blob(chunks, { type: type.split(";")[0] })
    const durationMs = Date.now() - startedAt - pausedMs - (pausedAt ? Date.now() - pausedAt : 0)
    void (ext === "webm" ? fixWebmDuration(raw, durationMs) : Promise.resolve(raw)).then((blob) =>
      opts.onDone({ file: new File([blob], `screen-recording-${stamp}.${ext}`, { type: raw.type }), durationSec: Math.round(durationMs / 1000), hasMic: micTracks.length > 0, hasSystemAudio: systemTracks.length > 0 }),
    )
  }
  video.addEventListener("ended", () => recorder.state !== "inactive" && recorder.stop())
  const limit = setTimeout(() => recorder.state !== "inactive" && recorder.stop(), MAX_RECORDING_SEC * 1000)
  recorder.start(1000)
  // клики, перетаскивания и прокрутка рисуются поверх страницы и попадают в запись вкладки, окна или экрана
  hideMarks = showInteractionOverlay()

  return {
    stop: () => recorder.state !== "inactive" && recorder.stop(),
    cancel: () => {
      cancelled = true
      if (recorder.state !== "inactive") recorder.stop()
      else release()
    },
    pause: () => {
      if (recorder.state !== "recording") return
      recorder.pause()
      pausedAt = Date.now()
    },
    resume: () => {
      if (recorder.state !== "paused") return
      recorder.resume()
      if (pausedAt) pausedMs += Date.now() - pausedAt
      pausedAt = null
    },
    get paused() {
      return recorder.state === "paused"
    },
    startedAt,
    hasMic: micTracks.length > 0,
    hasSystemAudio: systemTracks.length > 0,
    micError,
    mode: "display",
    displaySurface: (s as MediaTrackSettings & { displaySurface?: string }).displaySurface ?? null,
  }
}

/** Источник снимка: экран уже выбран, кадр снимается по команде пользователя */
export interface ScreenshotSource {
  grab: () => Promise<File>
  close: () => void
}

/**
 * Снимок экрана в два шага: сначала выбор вкладки/окна (getDisplayMedia), потом кадр по grab() —
 * пользователь успевает открыть нужный экран. PNG (WebP, если браузер умеет).
 * onEnded — пользователь нажал «Прекратить доступ» в браузере.
 */
export async function openScreenshotSource(onEnded?: () => void): Promise<ScreenshotSource> {
  if (!screenCaptureSupported()) throw new Error("Браузер не умеет делать снимок экрана")
  let stream: MediaStream
  try {
    stream = await navigator.mediaDevices.getDisplayMedia({ ...displayOptions(false), video: true } as DisplayMediaOptions)
  } catch (e) {
    if ((e as { name?: string })?.name === "NotAllowedError" || (e as { name?: string })?.name === "AbortError") throw new CaptureCancelled()
    throw new Error(`Не удалось сделать снимок: ${describeError(e, "screen")}`)
  }
  const close = () => stream.getTracks().forEach((t) => t.stop())
  const el = previewElement(stream)
  try {
    await el.play()
  } catch (e) {
    close()
    throw new Error(`Не удалось сделать снимок: ${describeError(e, "screen")}`)
  }
  await limitFrameSize(stream.getVideoTracks()[0], el, SCREENSHOT_MAX)
  stream.getVideoTracks()[0]?.addEventListener("ended", () => onEnded?.())
  return {
    close,
    grab: async () => {
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)))
      const canvas = document.createElement("canvas")
      canvas.width = el.videoWidth
      canvas.height = el.videoHeight
      canvas.getContext("2d")!.drawImage(el, 0, 0)
      const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, "image/webp", 0.92))
      const png = !blob || blob.type !== "image/webp" ? await new Promise<Blob | null>((r) => canvas.toBlob(r, "image/png")) : null
      const out = png ?? blob
      if (!out) throw new Error("Не удалось сохранить снимок")
      const stamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, "-")
      return new File([out], `screenshot-${stamp}.${out.type === "image/webp" ? "webp" : "png"}`, { type: out.type })
    },
  }
}
